import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import {
  api,
  NO_STATUS,
  queryString,
  type AnalysisStatus,
  type RestockRow,
  type StockSummary,
} from '../api/client.js';
import { DataTable } from '../components/DataTable.js';
import { AnalysisBanner } from '../components/AnalysisBanner.js';
import { SkuStatus } from '../components/SkuStatus.js';
import { ProductPageLink } from '../components/ProductPageLink.js';
import { Alert, Card, PageHeader, Select, Spinner, StockNote } from '../components/ui.js';
import { day, decimal, integer } from '../format.js';
import { familyPath } from '../lib/families.js';
import {
  compareInteger,
  compareIsoDate,
  compareNumericString,
  compareText,
  sortingFnFor,
} from '../lib/table-sort.js';

/**
 * The restock ranking. Every SKU comes down at once and the table sorts and
 * pages on the client; the CSV link hands the same rows to a spreadsheet. The
 * columns are defined in docs/analysis.md. Headers are abbreviated to fit the
 * card, with the full name in a tooltip. Every column names an explicit
 * comparator, and nullable columns map null to undefined so nulls sort last.
 */

const columnHelper = createColumnHelper<RestockRow>();

/** An abbreviated header whose full name is in a `title` tooltip. */
function abbr(label: string, full: string): ReactNode {
  return <span title={full}>{label}</span>;
}

/** Readable text for each data-quality flag, shown in the marker's tooltip. */
const DATA_QUALITY_LABELS: Record<string, string> = {
  stock_estimated:
    'On-hand is a forward estimate (no census inside ledger coverage) — sell-through is left out',
  negative_balance: 'On-hand went negative — the ledger is missing coverage',
  ledger_gaps: 'The ledger has a 7+ day coverage gap; on-hand is carried across it',
  no_ledger_data: 'No ledger data — the score uses the active-month average',
  no_sellable_ledger: 'Ledger rows exist but never a sellable balance — score uses active months',
  sales_before_ledger: 'This SKU sold before its ledger begins; early history is unknown',
  short_in_stock_window: 'Fewer than 14 in-stock days — velocity is floored',
  short_history: 'Fewer than 30 known days — the figure rests on little data',
  absent_from_census:
    'Absent from the census but moved recently — likely a suppressed listing still holding stock',
  ledger_behind: 'The ledger is behind the latest census; recent stock is unknown',
};

/** Short labels for the score basis, with the full explanation in a tooltip. */
const BASIS_LABEL: Record<RestockRow['scoreBasis'], string> = {
  in_stock_velocity: 'In-stock',
  active_months: 'Monthly',
};
const BASIS_FULL: Record<RestockRow['scoreBasis'], string> = {
  in_stock_velocity: 'Score base: units per 30 in-stock days, from the last stocked window',
  active_months: 'Score base: average units per active month (no sellable ledger history)',
};

function dataQualityTitle(flags: string[]): string {
  return flags.map((f) => DATA_QUALITY_LABELS[f] ?? f).join('\n');
}

const COLUMNS = [
  columnHelper.accessor('sellerSku', {
    header: 'SKU',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => {
      const flags = c.row.original.dataQuality;
      return (
        <span className="font-medium text-strong">
          {c.getValue()}
          {flags.length > 0 && (
            <span
              className="ml-1 cursor-help text-amber-600 dark:text-amber-400"
              title={dataQualityTitle(flags)}
              aria-label={`Data quality: ${dataQualityTitle(flags)}`}
            >
              ⚠
            </span>
          )}
        </span>
      );
    },
  }),
  columnHelper.accessor((r) => r.title ?? undefined, {
    id: 'title',
    header: 'Title',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => (
      <span className="block max-w-[11rem] truncate" title={c.getValue()}>
        {c.getValue() ?? '—'}
      </span>
    ),
  }),
  columnHelper.accessor((r) => r.status ?? undefined, {
    id: 'status',
    header: 'Status',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => (
      <SkuStatus status={c.row.original.status} delistedOn={c.row.original.delistedOn} />
    ),
  }),
  columnHelper.accessor((r) => r.family?.name ?? undefined, {
    id: 'family',
    header: 'Family',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => {
      const row = c.row.original;
      const family = row.family;
      // Stop the row click (which opens the SKU) so the links open the family and
      // the product page (the child ASIN, every SKU on it).
      return (
        <span className="flex items-center gap-2">
          {family ? (
            <Link
              to={familyPath(family.key)}
              onClick={(e) => e.stopPropagation()}
              className="block max-w-[11rem] truncate text-brand-600 hover:underline"
              title={family.name || family.key}
            >
              {family.name || family.key}
            </Link>
          ) : (
            '—'
          )}
          <ProductPageLink asin={row.asin} skuId={row.id} />
        </span>
      );
    },
  }),
  columnHelper.accessor((r) => r.quantity ?? undefined, {
    id: 'quantity',
    header: () => abbr('Qty', 'Quantity on hand'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue() ?? null),
  }),
  columnHelper.accessor('units30', {
    header: () => abbr('U30', 'Units sold, last 30 days'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor('units90', {
    header: () => abbr('U90', 'Units sold, last 90 days'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor('units365', {
    header: () => abbr('U365', 'Units sold, last 365 days'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor('unitsLifetime', {
    header: () => abbr('Life', 'Units sold, lifetime'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor('activeMonths', {
    header: () => abbr('Act. mo', 'Active months in the last 24 with a sale'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor('avgUnitsActiveMonth', {
    header: () => abbr('Avg/mo', 'Average units per active month'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareNumericString),
    cell: (c) => decimal(c.getValue(), 2),
  }),
  columnHelper.accessor((r) => r.unitsPer30InStock ?? undefined, {
    id: 'unitsPer30InStock',
    header: () => abbr('U/30 IS', 'Units per 30 in-stock days, last stocked 90-day window'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareNumericString),
    cell: (c) => decimal(c.getValue() ?? null, 2),
  }),
  columnHelper.accessor((r) => r.sellThroughLastStocked ?? undefined, {
    id: 'sellThroughLastStocked',
    header: () => abbr('ST (LS)', 'Sell-through of the last stocked 90-day window'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareNumericString),
    cell: (c) => decimal(c.getValue() ?? null, 2),
  }),
  columnHelper.accessor('daysInStock365', {
    header: () => abbr('IS 365', 'Days in stock, last 365'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor('stockoutDays365', {
    header: () => abbr('OOS 365', 'Days out of stock, last 365'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor((r) => r.lastInStockDay ?? undefined, {
    id: 'lastInStockDay',
    header: () => abbr('Last IS', 'Last day the SKU was in stock'),
    sortingFn: sortingFnFor(compareIsoDate),
    cell: (c) => day(c.getValue()),
  }),
  columnHelper.accessor((r) => r.lastSaleDate ?? undefined, {
    id: 'lastSaleDate',
    header: () => abbr('Last sale', 'Date of the last sale'),
    sortingFn: sortingFnFor(compareIsoDate),
    cell: (c) => day(c.getValue()),
  }),
  columnHelper.accessor((r) => r.daysSinceLastSale ?? undefined, {
    id: 'daysSinceLastSale',
    header: () => abbr('Days out', 'Days since the last sale'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue() ?? null),
  }),
  columnHelper.accessor('sessionsSinceLastSale', {
    header: () =>
      abbr(
        'Sess. since',
        'Sessions since the last sale. SKUs that share a listing each show that listing’s sessions; the family roll-up counts them once.',
      ),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor((r) => r.conversionInStock ?? undefined, {
    id: 'conversionInStock',
    header: () => abbr('Conv.', 'Conversion while in stock (units ordered ÷ sessions)'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareNumericString),
    cell: (c) => decimal(c.getValue() ?? null, 4),
  }),
  columnHelper.accessor('estLostUnits', {
    header: () => abbr('Est. lost', 'Estimated lost units while out of stock'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareNumericString),
    cell: (c) => decimal(c.getValue(), 0),
  }),
  columnHelper.accessor('score', {
    header: () => abbr('Score', 'Restock score; higher is a stronger candidate'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareNumericString),
    cell: (c) => <span className="font-medium text-strong">{decimal(c.getValue(), 2)}</span>,
  }),
  columnHelper.accessor('scoreBasis', {
    header: 'Basis',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => <span title={BASIS_FULL[c.getValue()]}>{BASIS_LABEL[c.getValue()]}</span>,
  }),
];

export function RestockPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [basis, setBasis] = useState('');

  const query = useQuery({
    queryKey: ['restock'],
    queryFn: () =>
      api.get<{
        rows: RestockRow[];
        stock: StockSummary;
        derived: AnalysisStatus;
        generatedAt: string;
      }>('/api/restock'),
  });

  // The suggested order quantity per SKU, looked up by id, so Restock can show
  // it beside the score without re-deriving it. hideDuplicates=false: Restock is
  // the whole catalog, so a hidden duplicate must still get its suggested column.
  const plan = useQuery({
    queryKey: ['purchase-plan', 'sku', false],
    queryFn: () =>
      api.get<{ rows: { id: number; suggestedQty: number }[] }>(
        '/api/purchase-plan?scope=sku&hideDuplicates=false',
      ),
  });
  const suggestedById = useMemo(() => {
    const map = new Map<number, number>();
    for (const row of plan.data?.rows ?? []) map.set(row.id, row.suggestedQty);
    return map;
  }, [plan.data]);

  const columns = useMemo(
    () => [
      ...COLUMNS,
      columnHelper.accessor((r) => suggestedById.get(r.id) ?? undefined, {
        id: 'suggestedQty',
        header: () => abbr('Sugg. qty', 'Suggested order quantity (see the Purchase plan)'),
        meta: { numeric: true },
        sortingFn: sortingFnFor(compareInteger),
        cell: (c) => integer(c.getValue() ?? null),
      }),
    ],
    [suggestedById],
  );

  // One option per status present in the ranking, plus a "(no status)" bucket
  // when some rows have none, each with its count; nulls last.
  const statusOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of query.data?.rows ?? []) {
      const key = row.status ?? NO_STATUS;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return [...counts.entries()].sort(([a], [b]) => {
      if (a === NO_STATUS) return 1;
      if (b === NO_STATUS) return -1;
      return a.localeCompare(b);
    });
  }, [query.data]);

  const rows = useMemo(
    () =>
      (query.data?.rows ?? [])
        .filter((r) =>
          !status ? true : status === NO_STATUS ? r.status === null : r.status === status,
        )
        .filter((r) => (!basis ? true : r.scoreBasis === basis)),
    [query.data, status, basis],
  );

  const csvHref = `/api/restock.csv${queryString({ status, basis })}`;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Restock"
        description="Every SKU scored by how it sold when it was selling and how much demand has gone unmet since. Higher is a stronger restock candidate; see docs/analysis.md for the formula."
      />

      <AnalysisBanner />

      <p className="text-xs text-muted">
        Basis: <span className="font-medium text-body">In-stock</span> scores on units per 30
        in-stock days from the last stocked window;{' '}
        <span className="font-medium text-body">Monthly</span> scores on the average units per
        active month when there is no sellable ledger history. A ⚠ marks a row whose stock history
        is uncertain.
      </p>

      {query.isPending && <Spinner label="Ranking SKUs" />}
      {query.isError && <Alert>{query.error.message}</Alert>}
      {query.data && (
        <Card>
          <DataTable
            data={rows}
            columns={columns}
            initialSorting={[{ id: 'score', desc: true }]}
            storageKey="restock"
            resetKey={`${status}|${basis}`}
            dense
            stickyFirstColumn
            onRowClick={(row) => void navigate(`/skus/${row.id}`)}
            emptyText="Nothing to rank yet. Import an All Listings report and an All Orders file."
            toolbar={
              <>
                <span className="text-xs text-muted">
                  {integer(rows.length)} SKU{rows.length === 1 ? '' : 's'}
                </span>
                <div className="w-44">
                  <Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    aria-label="Status"
                  >
                    <option value="">Any status</option>
                    {statusOptions.map(([s, count]) => (
                      <option key={s} value={s}>
                        {s === NO_STATUS ? '(no status)' : s} ({integer(count)})
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="w-40">
                  <Select
                    value={basis}
                    onChange={(e) => setBasis(e.target.value)}
                    aria-label="Basis"
                  >
                    <option value="">Any basis</option>
                    <option value="in_stock_velocity">In-stock</option>
                    <option value="active_months">Monthly</option>
                  </Select>
                </div>
                <StockNote source={query.data.stock.source} asOf={query.data.stock.asOf} />
                <a
                  href={csvHref}
                  className="inline-flex items-center rounded-md bg-surface px-3 py-2 text-sm font-medium text-body ring-1 ring-inset ring-line-strong hover:bg-sunken"
                >
                  Download CSV
                </a>
              </>
            }
          />
        </Card>
      )}
    </div>
  );
}
